import { ref } from 'vue'

import { Errors } from '@/libs/error'

/**
 * Whether this hotel has the module this portal is the face of.
 *
 * This portal is `operations` — housekeeping and maintenance — and from this release
 * the API refuses every route of a module the hotel has not bought with 403 and
 * `{"error": {"code": 19}}` (docs/MODULI.md §3.2). Without this the portal would draw
 * an empty round: no tasks, no rooms, no explanation, and a housekeeper reloading it
 * in a corridor because nothing looks broken.
 *
 * It is a module-level ref and not a store field on purpose. `stores/fetch.ts` is what
 * notices the refusal, and it already reaches for the app store — but the answer has to
 * be readable from `App.vue` before any store is resolved, and it must survive the
 * signing-out that a 401 does. The same shape `libs/toast.ts` already uses.
 *
 * Nothing clears it on a timer, and there is no retry: the licence is changed in the
 * vendor console, not in this browser, and a portal that kept asking would be a phone
 * calling a locked door every few seconds for the rest of the shift.
 *
 * It is cleared only where the answer can honestly have changed — signing out and
 * signing in (`stores/app.ts`), which is the one thing a person on this device can do
 * about it. Without that the flag outlived the account it was recorded for: a second
 * housekeeper signing in on the same tablet met the first one's refusal, and so did
 * everybody on it after the hotel bought the module.
 */
export const moduleUnlicensed = ref(false)

/**
 * Forgets a refusal, because it belonged to a session that is over.
 *
 * Called when the identity behind the calls changes — never on a timer and never from
 * the notice by itself, which would be the retry loop this flag exists to avoid.
 */
export const clearModuleRefusal = () => {
    moduleUnlicensed.value = false
}

/**
 * Reads one refused answer and says whether it was this refusal.
 *
 * Both shapes of body are taken: the parsed object a `.json()` call hands to
 * `onFetchError`, and the raw text of a call that never asked for JSON.
 */
export const noteModuleRefusal = (status: number | undefined, body: unknown): boolean => {
    if (status !== 403) return false

    let parsed = body

    if (typeof parsed === 'string') {
        try {
            parsed = JSON.parse(parsed)
        } catch {
            // A 403 whose body is not JSON is an ordinary refusal, not this one.
            return false
        }
    }

    if ((parsed as { error?: { code?: number } })?.error?.code !== Errors.ModuleNotLicensed) return false

    moduleUnlicensed.value = true

    return true
}
